import type { Ref } from 'vue';
import { computed, ref, watch } from 'vue';
import { getCookie } from '@/utils/cookie';

/**
 * Persists the user's "Later" dismissal of the Place Suggestions banner.
 *
 * Rather than storing a timestamp or a simple flag, we remember the *set of
 * suggestion IDs* that were on screen when "Later" was clicked. The banner stays
 * hidden while every live suggestion is in that set, and resurfaces as soon as a
 * new suggestion ID appears that was never dismissed.
 *
 * The set is scoped per account so switching accounts shows the correct state,
 * and is pruned to the live suggestions so it can never grow unbounded.
 */

const KEY_PREFIX = 'place-suggestions-dismissed';

/**
 * Mirror `usePersonalLocalStorage`: when imitating an account we isolate to
 * sessionStorage so an admin's dismissals don't leak into the browser-wide
 * localStorage.
 */
function getStorage(): Storage {
  return getCookie('imitation_token')
    ? window.sessionStorage
    : window.localStorage;
}

function storageKey(accountId: number | null): string | null {
  return accountId == null ? null : `${KEY_PREFIX}:${accountId}`;
}

function readDismissed(accountId: number | null): string[] {
  const key = storageKey(accountId);
  if (!key) return [];
  try {
    const raw = getStorage().getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((v): v is string => typeof v === 'string')
      : [];
  } catch {
    return [];
  }
}

function writeDismissed(accountId: number | null, ids: string[]): void {
  const key = storageKey(accountId);
  if (!key) return;
  try {
    getStorage().setItem(key, JSON.stringify(ids));
  } catch {
    // Storage unavailable or over quota — dismissal simply won't persist.
  }
}

export function usePlaceSuggestionsDismissal(
  accountId: Ref<number | null>,
  suggestionIds: Ref<string[]>,
) {
  const dismissed = ref<Set<string>>(new Set(readDismissed(accountId.value)));

  // Reload the dismissed set when the active account changes (accounts can be
  // switched without a full page reload).
  watch(accountId, (id) => {
    dismissed.value = new Set(readDismissed(id));
  });

  // Prune IDs that are no longer in the live suggestions list so the stored set
  // stays bounded by the number of suggestions. Skipped while the list is empty
  // (query still loading, or no suggestions) so a momentary empty list can't
  // wipe a valid dismissal.
  watch(suggestionIds, (ids) => {
    if (ids.length === 0 || dismissed.value.size === 0) return;
    const live = new Set(ids);
    const pruned = [...dismissed.value].filter((id) => live.has(id));
    if (pruned.length !== dismissed.value.size) {
      dismissed.value = new Set(pruned);
      writeDismissed(accountId.value, pruned);
    }
  });

  // The banner is considered dismissed only while *every* live suggestion was
  // already dismissed. A single new ID resurfaces it.
  const isDismissed = computed(
    () =>
      suggestionIds.value.length > 0 &&
      suggestionIds.value.every((id) => dismissed.value.has(id)),
  );

  // Record exactly the currently-live suggestion IDs as dismissed. Replacing the
  // set (rather than merging) also prunes any stale IDs in one step.
  function dismiss() {
    const ids = [...suggestionIds.value];
    dismissed.value = new Set(ids);
    writeDismissed(accountId.value, ids);
  }

  return { isDismissed, dismiss };
}
